import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import Utils from '@iv/shared/utils';
import {
  Future,
  Instrument
} from '@iv/model/instrument';

type instrumentOrFuture = Instrument[] | Future[];
type service = { serviceId: string, name: string }[];
type tabAutocompleteItemType = instrumentOrFuture | service;
type tabItemMultiType = Instrument & Future & { serviceId: string, name: string };

@Component({
  selector: 'iv-live-tabs-autocomplete',
  templateUrl: './tabs-autocomplete.component.html',
  styleUrls: ['./tabs-autocomplete.component.scss']
})
export class TabsAutocompleteComponent implements OnInit, OnDestroy, AfterViewInit {

  constructor(
    private elementRef: ElementRef
  ) {
    this.offClickHandler = (() => {
      const handler = (event) => {
        if (!elementRef.nativeElement.contains(event.target)) {
          this.showDropdown = false;
        }
        document.removeEventListener('click', handler, true);
      };
      return handler;
    })();
  }

  @Input()
  public valueAttribute: string;
  @Input()
  public label = '';
  @Input()
  public cssClass = '';
  @Input()
  public type = '';
  @Input()
  public isFutPermission: boolean;
  @Input()
  public isStockPermissions: boolean;
  @Input()
  set instruments(value: instrumentOrFuture) {
    this._instruments = value;
  }
  @Input()
  set services(value: service) {
    this._services = value;
  }
  @Input() set visible(value) {
    if (value === true) {
      this.elementRef.nativeElement.getElementsByTagName('INPUT')[0].focus();
    }
    this._visible = value;
  }

  @Output()
  public selectItem = new EventEmitter<any>();

  public filteredData: tabAutocompleteItemType = [];
  public searchValue = '';
  public showDropdown = false;
  public isMac = Utils.isMac;

  private _DEFAULT_INSTRUMENTS = [9327, 627, 18155, 799, 555, 15553, 10562, 10565, 18367, 17776, 17605, 17777, 20593, 21485, 20558];
  private _DEFAULT_FUTURES = [680, 43, 6467, 6504, 5334, 2564];
  private readonly offClickHandler;
  private _instruments:  instrumentOrFuture = [];
  private _services: service = [];
  private _selectedData: any;
  private _visible;

  get instruments():  instrumentOrFuture {
    return this._instruments;
  }

  get services(): service {
    return this._services;
  }

  get selectedData(): any {
    return this._selectedData;
  }

  get visible() {
    return this._visible;
  }

  set selectedData(value: any) {
    this._selectedData = value;
  }

  public ngOnInit(): void {
    this._setInitialState();
  }

  public ngAfterViewInit(): void {
    window.setTimeout(() => {
      this.elementRef.nativeElement.getElementsByTagName('INPUT')[0].focus();
    }, 0);
  }

  public ngOnDestroy(): void {
    document.removeEventListener('click', this.offClickHandler);
  }

  public getValue(selectedData): string {
    if (selectedData === undefined || selectedData === null) {
      return '';
    }
    return selectedData.symbol ? selectedData.nameAndExchange : selectedData.name;
  }

  public toggleDropdown(event) {
    event.preventDefault();
    event.stopPropagation();
    this.showDropdown = !this.showDropdown;
    this.toggleEventListener(this.showDropdown);
  }

  public onItemClick(event, item) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedData = item;
    this.showDropdown = false;
  }

  public search() {
    if (this.searchValue) {
      const searchValue = this.searchValue.toLowerCase();
      const filteredInstrumentsAndFutures = this.instruments.filter(item => {
        if (!item.nameAndExchange) return false;
        return (item.nameAndExchange.toLowerCase().indexOf(searchValue, 0) === 0);
      }, this);

      const filteredServices = this.services.filter(item => {
        return (item.name.toLowerCase().indexOf(searchValue, 0) === 0);
      }, this);

      this.filteredData = [...filteredServices, ...filteredInstrumentsAndFutures]
        .sort((a: tabItemMultiType, b: tabItemMultiType) => {
          if (a.nameAndExchange > b.nameAndExchange) {
            return 1;
          }

          if (a.nameAndExchange < b.nameAndExchange) {
            return -1;
          }
          return 0;
        }) as service & instrumentOrFuture;
    } else {
      this._setInitialState();
    }
    this.showDropdown = true;
  }

  public onAddClick() {
    this.selectItem.emit(this.selectedData);
    this._setInitialState();
    this.showDropdown = false;
  }

  public getStockType(item: any) {
    return Utils.getStockType(item);
  }

  private toggleEventListener(showDropdown: boolean) {
    if (showDropdown) {
      document.addEventListener('click', this.offClickHandler);
    } else {
      document.removeEventListener('click', this.offClickHandler);
    }
  }

  private _setInitialState() {
    this.selectedData = null;
    const filteredServices = this.services;
    const filteredInstruments = [];
    const defaultServicesArr = (): number[] => {
      if (this.isStockPermissions && this.isFutPermission) {
        return [...this._DEFAULT_INSTRUMENTS, ...this._DEFAULT_FUTURES];
      }

      if (this.isStockPermissions) {
        return this._DEFAULT_INSTRUMENTS;
      }

      if (this.isFutPermission) {
        return this._DEFAULT_FUTURES;
      }
    };

    defaultServicesArr().forEach(instrumentId => {
      const instrumentToAdd = this.instruments.find((instrument: Instrument & Future) => {
        if (instrument.underlyingId && this.isFutPermission) {
          return instrument.underlyingId === instrumentId;
        }

        if (instrument.stockId && this.isStockPermissions && instrumentId !== 2564) {  // 2564 Дублируется и для стоков и для фьючей
          return instrument.stockId === instrumentId;
        }
      });
      if (instrumentToAdd) {
        filteredInstruments.push(instrumentToAdd);
      }
    }, this);
    this.filteredData = [...filteredServices, ...filteredInstruments];
  }
}
